import { AlertTriangle, CheckCircle2, LoaderCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  buildProjectStoryImportReviewRoute,
  getStoryImportPassDisplayLabel,
  getStoryImportProgressPercent,
  getStoryImportProgressValue,
} from "@/features/story-import/storyImportMappers";
import { useStoryImportStore } from "@/stores/storyImportStore";

export function ImportProgressBar() {
  const { t } = useTranslation();
  const cancelProcessing = useStoryImportStore((state) => state.cancelProcessing);
  const currentJob = useStoryImportStore((state) => state.currentJob);
  const dismissedJobIds = useStoryImportStore((state) => state.dismissedJobIds);
  const dismissProgressForJob = useStoryImportStore((state) => state.dismissProgressForJob);
  const isCanceling = useStoryImportStore((state) => state.isCanceling);
  const isRetrying = useStoryImportStore((state) => state.isRetrying);
  const retryProcessing = useStoryImportStore((state) => state.retryProcessing);

  if (!currentJob || dismissedJobIds.includes(currentJob.jobId)) {
    return null;
  }

  if (currentJob.status === "complete" || currentJob.status === "completed") {
    return null;
  }

  const isCanceled = currentJob.status === "canceled";
  const isFailed = currentJob.status === "failed";
  const isComplete = currentJob.status === "review_ready";
  const isCancelable =
    currentJob.status === "queued"
    || currentJob.status === "pending"
    || currentJob.status === "processing"
    || currentJob.status === "canceling";
  const progressValue = getStoryImportProgressValue(currentJob);
  const progressPercent = getStoryImportProgressPercent(currentJob);

  return (
    <section
      className="mb-6 rounded-[1.75rem] border border-ink/10 bg-white/80 px-5 py-4 shadow-vellum"
      role={isFailed || isCanceled ? "alert" : "status"}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex items-center gap-3">
            {isFailed ? (
              <AlertTriangle className="h-5 w-5 shrink-0 text-rose-600" />
            ) : isCanceled ? (
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
            ) : isComplete ? (
              <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
            ) : (
              <LoaderCircle className="h-5 w-5 shrink-0 animate-spin text-clay" />
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold uppercase tracking-[0.18em] text-clay">
                {isFailed
                  ? t("storyImport.progress.failed")
                  : isCanceled
                    ? t("storyImport.progress.canceled")
                  : isComplete
                    ? t("storyImport.progress.ready")
                    : t("storyImport.progress.active")}
              </p>
              <p className="truncate text-base font-semibold text-foreground">
                {currentJob.sourceFileName ?? t("storyImport.progress.defaultTitle")}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
              <span>{getStoryImportPassDisplayLabel(currentJob) ?? t("storyImport.processing.status.processing")}</span>
              <span>
                {t("storyImport.processing.passProgress", {
                  current: progressValue,
                  total: currentJob.totalPasses,
                })}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-ink/10">
              <div
                className={`h-full rounded-full transition-all ${
                  isFailed
                    ? "bg-rose-500"
                    : isCanceled
                      ? "bg-amber-500"
                      : isComplete
                        ? "bg-emerald-500"
                        : "bg-ink"
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            {currentJob.detectedLanguage ? (
              <p className="text-sm text-muted-foreground">
                {t("storyImport.processing.detectedLanguage", {
                  language: currentJob.detectedLanguage,
                })}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          {isFailed ? (
            <Button
              disabled={isRetrying}
              onClick={() => {
                void retryProcessing().catch(() => undefined);
              }}
              type="button"
              variant="outline"
            >
              {t("storyImport.actions.retry")}
            </Button>
          ) : null}
          {isCancelable ? (
            <Button
              disabled={isCanceling}
              onClick={() => {
                void cancelProcessing().catch(() => undefined);
              }}
              type="button"
              variant="outline"
            >
              {t("storyImport.actions.cancelImport")}
            </Button>
          ) : null}
          {isComplete && currentJob.projectId ? (
            <Button asChild type="button">
              <Link to={buildProjectStoryImportReviewRoute(currentJob.projectId, currentJob.jobId)}>
                {t("storyImport.actions.openReview")}
              </Link>
            </Button>
          ) : null}
          <Button
            onClick={() => dismissProgressForJob(currentJob.jobId)}
            type="button"
            variant="ghost"
          >
            {t("projects.actions.dismiss")}
          </Button>
        </div>
      </div>
    </section>
  );
}
